import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/0529fec7-0ce1-4229-bf36-7b88d48e13e7/images/00f779c2e11a8ff5a60ea7dd369eeecc458bf2d48baebbf933ddb9ea74912b6e.png";

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden flex items-center pt-28 pb-20">
      {/* Background motorcycle */}
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Premium Motorcycle" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0F12]/40 via-[#0D0F12]/70 to-[#0D0F12]" />
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute inset-0 scanline pointer-events-none" />
      </div>

      {/* Animated scan line */}
      <div className="absolute inset-y-0 left-0 right-0 overflow-hidden pointer-events-none">
        <div className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#00cccc] to-transparent animate-scan opacity-70" style={{ boxShadow: "0 0 30px #00cccc" }} />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full">
        {/* LEFT */}
        <div className="max-w-3xl">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl xl:text-[4.2rem] font-black leading-[1.05] tracking-tight"
          >
            Your Next Ride <br />
            <span className="text-gradient-cyan">Starts Here.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-7 max-w-xl text-lg text-zinc-400 leading-relaxed"
          >
            Biker Boulevard&rsquo;s Smart Valuation Engine analyzes key vehicle details and condition to deliver data-driven price estimates, helping both buyers and sellers make fair, transparent, and informed deals with confidence.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-9 flex flex-col sm:flex-row sm:flex-wrap gap-4"
          >
            <Link
              to="/marketplace"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00cccc] text-black font-semibold rounded-full hover:shadow-[0_0_36px_rgba(0,204,204,0.7)] transition-all duration-300"
            >
              Explore Bikes
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/list-my-bike"
              data-testid="hero-secondary-cta"
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00cccc] text-black font-semibold rounded-full hover:shadow-[0_0_36px_rgba(0,204,204,0.7)] transition-all duration-300"
            >
              List My Bike
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/sell-my-bike"
              data-testid="hero-cta-sell-direct"
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00cccc] text-black font-semibold rounded-full hover:shadow-[0_0_36px_rgba(0,204,204,0.7)] transition-all duration-300"
            >
              Sell directly to Biker Boulevard
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>

          {/* Stats strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-12 grid grid-cols-2 gap-6 max-w-sm"
          >
            {[
              { v: "10 MIN", l: "Avg. Valuation" },
              { v: "98%", l: "Price Accuracy" },
            ].map((s) => (
              <div key={s.l} data-testid={`hero-stat-${s.l.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="text-2xl font-mono font-bold text-white">{s.v}</div>
                <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
